import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ProjectComparisonDialog } from '../components/ProjectComparisonDialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Plus, Kanban, Building2, Euro, Calendar,
  ChevronRight, GripVertical, Trash2, Edit, Eye, RefreshCw, Clock, FileDown, BarChart3,
  Calculator, ClipboardCheck, ExternalLink, FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { downloadBlob } from '../utils/download';
import { AttachDocumentButton } from '../components/AttachDocumentButton';

const API = ((typeof window !== "undefined" && window.__API_URL__) || process.env.REACT_APP_BACKEND_URL) + '/api';

const STATUS_CONFIG = {
  prospection: { label: 'Prospection', color: '#71717A', bgClass: 'bg-zinc-500/10 border-zinc-500/30' },
  en_cours_achat: { label: 'Offre en cours', color: '#F59E0B', bgClass: 'bg-amber-500/10 border-amber-500/30' },
  compromis_signe: { label: 'Compromis signé', color: '#EAB308', bgClass: 'bg-yellow-500/10 border-yellow-500/30' },
  achete: { label: 'Acte signé', color: '#3B82F6', bgClass: 'bg-blue-500/10 border-blue-500/30' },
  en_renovation: { label: 'En rénovation', color: '#A855F7', bgClass: 'bg-purple-500/10 border-purple-500/30' },
  en_vente: { label: 'En vente', color: '#F97316', bgClass: 'bg-orange-500/10 border-orange-500/30' },
  vendu: { label: 'Vendu', color: '#10B981', bgClass: 'bg-emerald-500/10 border-emerald-500/30' },
};

// Ordre canonique pour l'indicateur "Étape N / 7"
const STATUS_ORDER = ['prospection', 'en_cours_achat', 'compromis_signe', 'achete', 'en_renovation', 'en_vente', 'vendu'];

const formatDuration = (start, end) => {
  if (!start) return null;
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  const days = Math.max(0, Math.floor((e - s) / (1000 * 60 * 60 * 24)));
  if (days < 7) return `${days}j`;
  const weeks = Math.floor(days / 7);
  if (weeks < 10) return `${weeks}sem ${days % 7}j`;
  const months = Math.floor(days / 30);
  return `${months} mois`;
};

const handlePdfExport = async (projectId) => {
  try {
    const res = await axios.get(`${API}/projects/${projectId}/pdf`, { withCredentials: true, responseType: 'blob' });
    downloadBlob(res.data, `rapport_projet_${projectId.slice(0, 8)}.pdf`, 'application/pdf');
    toast.success('PDF téléchargé');
  } catch {
    toast.error('Erreur export PDF');
  }
};

const handleBankDossierExport = async (projectId) => {
  try {
    toast.info('Génération du dossier de financement…');
    const res = await axios.get(`${API}/projects/${projectId}/bank-dossier-pdf`, { withCredentials: true, responseType: 'blob' });
    downloadBlob(res.data, `dossier_financement_${projectId.slice(0, 8)}.pdf`, 'application/pdf');
    toast.success('Dossier banque généré');
  } catch (err) {
    const msg = err?.response?.data?.detail || 'Erreur génération dossier';
    toast.error(String(msg).slice(0, 140));
  }
};

const ProjectCard = ({ project, onEdit, onDelete, onStatusChange, onCompare, onNav, onDocAttached, highlight }) => {
  const statusConfig = STATUS_CONFIG[project.status] || STATUS_CONFIG.prospection;
  const startDate = project.achat_date || project.created_at;
  const endDate = project.status === 'vendu' ? project.vente_date : null;
  const duration = formatDuration(startDate, endDate);
  
  return (
    <div 
      className={`bg-zinc-900 border rounded-lg p-3 mb-3 cursor-grab active:cursor-grabbing hover:border-zinc-600 transition-colors ${statusConfig.bgClass} ${highlight ? 'ring-2 ring-emerald-500' : ''}`}
      draggable
      onDragStart={(e) => e.dataTransfer.setData('projectId', project.id)}
      data-testid={`project-card-${project.id}`}
      id={`project-${project.id}`}
    >
      <div className="flex items-start gap-2 mb-2">
        <GripVertical className="w-4 h-4 text-zinc-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-white text-sm truncate">{project.property_title || 'Sans titre'}</h4>
          <p className="text-xs text-zinc-500">{project.property_city}</p>
          {(() => {
            const stepIdx = STATUS_ORDER.indexOf(project.status);
            const stepNum = stepIdx >= 0 ? stepIdx + 1 : 1;
            return (
              <div className="mt-1.5" title={`Étape ${stepNum} sur ${STATUS_ORDER.length}`}>
                <div className="flex gap-0.5" data-testid={`project-step-${project.id}`}>
                  {STATUS_ORDER.map((s, i) => (
                    <div
                      key={s}
                      className="h-1 flex-1 rounded-full"
                      style={{ backgroundColor: i <= stepIdx ? statusConfig.color : '#27272A' }}
                    />
                  ))}
                </div>
                <span className="text-[9px] text-zinc-500 mt-0.5 block">Étape {stepNum} / {STATUS_ORDER.length}</span>
              </div>
            );
          })()}
        </div>
      </div>
      
      {(() => {
        const displayPrice = project.prix_achat_reel || project.property_price;
        const isReal = project.prix_achat_reel && project.status !== 'prospection' && project.status !== 'negociation';
        return displayPrice ? (
          <div className="flex items-center gap-1 text-sm font-mono mb-2" style={{ color: statusConfig.color }}>
            <Euro className="w-3 h-3" />
            {displayPrice.toLocaleString('fr-FR')}
            {isReal && <span className="text-[9px] text-emerald-400 ml-1">(réel)</span>}
          </div>
        ) : null;
      })()}

      {/* Latest offer (envoyée / en négociation / acceptée) — affiche le prix de l'offre réelle */}
      {project.offre_active_prix && project.offre_active_status && project.offre_active_status !== 'refusee' && project.offre_active_status !== 'retiree' && (
        <div
          className={`text-[11px] font-mono mb-2 px-2 py-1 rounded border ${
            project.offre_active_status === 'acceptee'
              ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
              : 'bg-blue-500/10 text-blue-400 border-blue-500/20'
          }`}
          data-testid={`project-offre-active-${project.id}`}
        >
          {project.offre_active_status === 'acceptee' ? 'Offre acceptée' : 'Offre en cours'} :{' '}
          <span className="font-semibold">{project.offre_active_prix.toLocaleString('fr-FR')} €</span>
        </div>
      )}

      {/* En vente: show asking price */}
      {project.status === 'en_vente' && project.prix_mise_en_vente && (
        <div className="text-[11px] font-mono mb-2 px-2 py-1 rounded bg-orange-500/10 text-orange-400 border border-orange-500/20" data-testid={`project-mise-en-vente-${project.id}`}>
          Mise en vente : <span className="font-semibold">{project.prix_mise_en_vente.toLocaleString('fr-FR')} €</span>
        </div>
      )}

      {/* Vendu: show final sale price */}
      {project.status === 'vendu' && project.prix_vente_reel && (
        <div className="text-[11px] font-mono mb-2 px-2 py-1 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" data-testid={`project-prix-vente-${project.id}`}>
          Vendu : <span className="font-semibold">{project.prix_vente_reel.toLocaleString('fr-FR')} €</span>
        </div>
      )}

      {/* Real travaux cost (en_vente or vendu) */}
      {(project.status === 'en_vente' || project.status === 'vendu') && project.travaux_reels > 0 && (
        <div className="text-[11px] font-mono mb-2 px-2 py-1 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20" data-testid={`project-travaux-reels-${project.id}`}>
          Travaux réels : <span className="font-semibold">{project.travaux_reels.toLocaleString('fr-FR')} €</span>
        </div>
      )}

      {duration && (
        <div className="flex items-center gap-1 text-[11px] text-zinc-500 mb-2" data-testid="project-duration">
          <Clock className="w-3 h-3" />
          <span>{endDate ? 'Durée totale :' : 'En cours :'} {duration}</span>
        </div>
      )}

      {/* Écart Prévu vs Réel - affiché dès qu'il y a au moins 1 ligne comparable */}
      {(() => {
        const rows = [];
        // ACHAT : prévu (étude) vs réel (offre/acte)
        const prevAchat = project.prix_achat_prevu || project.property_price;
        if (prevAchat && project.prix_achat_reel) {
          rows.push({ label: 'Achat', prev: prevAchat, reel: project.prix_achat_reel, kind: 'achat' });
        }
        // TRAVAUX : prévu (étude) vs engagé (BDC validés) vs dépensé (factures)
        const prevTx = project.budget_prevu_travaux;
        const engageTx = project.travaux_engage || 0;
        const depenseTx = project.travaux_depense || 0;
        // "Réel" : priorité dépenses > engagements > travaux_reels (legacy, seulement vendu)
        const reelTx = depenseTx > 0 ? depenseTx : (engageTx > 0 ? engageTx : project.travaux_reels);
        const reelLabel = depenseTx > 0 ? 'dépensé' : (engageTx > 0 ? 'engagé' : 'réel');
        if (prevTx && reelTx) {
          rows.push({ label: 'Travaux', prev: prevTx, reel: reelTx, kind: 'travaux', sublabel: reelLabel });
        }
        // REVENTE : prévu vs vendu
        const prevRevente = project.prix_revente_prevu || project.prix_mise_en_vente;
        if (prevRevente && project.prix_vente_reel) {
          rows.push({ label: 'Revente', prev: prevRevente, reel: project.prix_vente_reel, kind: 'revente' });
        }
        if (rows.length === 0) return null;
        return (
          <div className="mb-2 px-2 py-1.5 rounded bg-zinc-950/60 border border-zinc-800" data-testid={`project-ecart-${project.id}`}>
            <div className="text-[9px] text-zinc-500 uppercase tracking-wider mb-1">Prévu vs Réel</div>
            <div className="space-y-0.5">
              {rows.map((r) => {
                const diff = r.reel - r.prev;
                const pct = r.prev > 0 ? (diff / r.prev) * 100 : 0;
                // Travaux dépassé = rouge, Achat plus cher = rouge, Revente en dessous = rouge
                const isBad = r.kind === 'travaux' ? diff > 0 : (r.kind === 'achat' ? diff > 0 : diff < 0);
                const color = Math.abs(pct) < 2 ? 'text-zinc-400' : (isBad ? 'text-red-400' : 'text-emerald-400');
                return (
                  <div
                    key={r.label}
                    className="flex items-center justify-between text-[10px] font-mono"
                    title={r.kind === 'travaux'
                      ? `Prévu (étude rentabilité, travaux + ${(((r.prev / Math.max(1, project.travaux_estimation || r.prev)) - 1) * 100).toFixed(0)}% imprévus + diagnostics)\nRéel (${r.sublabel}) = somme des ${r.sublabel === 'dépensé' ? 'factures' : 'BDC validés'}`
                      : r.kind === 'achat'
                      ? 'Prévu = prix de la fiche bien / étude. Réel = offre acceptée ou acte authentique.'
                      : 'Prévu = prix de revente estimé. Réel = prix de vente réel.'
                    }
                  >
                    <span className="text-zinc-500">
                      {r.label}
                      {r.sublabel && <span className="text-zinc-600 ml-1">({r.sublabel})</span>}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-zinc-400">{Math.round(r.prev).toLocaleString('fr-FR')}</span>
                      <span className="text-zinc-600">→</span>
                      <span className="text-zinc-200">{Math.round(r.reel).toLocaleString('fr-FR')}</span>
                      <span className={`${color} min-w-[40px] text-right`}>
                        {diff >= 0 ? '+' : ''}{pct.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}
      
      {project.status === 'vendu' && project.plus_value_reelle !== undefined && (
        <div className={`text-xs font-mono px-2 py-1 rounded ${project.plus_value_reelle >= 0 ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
          PV: {project.plus_value_reelle >= 0 ? '+' : ''}{project.plus_value_reelle.toLocaleString('fr-FR')} €
        </div>
      )}

      {/* Document attachments : compromis & acte selon statut */}
      {(['compromis_signe', 'achete', 'en_renovation', 'en_vente', 'vendu'].includes(project.status)) && (
        <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-zinc-800/60" onClick={(e) => e.stopPropagation()}>
          <AttachDocumentButton
            projectId={project.id}
            folder="compromis"
            label="Compromis"
            fileId={project.compromis_file_id}
            testId={`pipeline-attach-compromis-${project.id}`}
            onAttached={async (fileId) => {
              await axios.patch(`${API}/projects/${project.id}/attach-document?kind=compromis&file_id=${fileId}`, {}, { withCredentials: true });
              onDocAttached?.();
            }}
          />
          {['achete', 'en_renovation', 'en_vente', 'vendu'].includes(project.status) && (
            <AttachDocumentButton
              projectId={project.id}
              folder="acte_vente"
              label="Acte authentique"
              fileId={project.acte_file_id}
              testId={`pipeline-attach-acte-${project.id}`}
              onAttached={async (fileId) => {
                await axios.patch(`${API}/projects/${project.id}/attach-document?kind=acte&file_id=${fileId}`, {}, { withCredentials: true });
                onDocAttached?.();
              }}
            />
          )}
        </div>
      )}

      {/* Cross-navigation shortcuts */}
      {project.property_id && (
        <div className="flex flex-wrap gap-1 mt-2 pt-2 border-t border-zinc-800/60">
          <button onClick={(e) => { e.stopPropagation(); onNav(`/recherche?property_id=${project.property_id}`); }}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-zinc-700 text-[10px] text-zinc-400 hover:text-white"
            data-testid={`nav-property-${project.id}`}
            title="Fiche bien">
            <Building2 className="w-3 h-3" /> Bien
          </button>
          <button onClick={(e) => { e.stopPropagation(); onNav(project.study_id ? `/etude?study_id=${project.study_id}` : `/etude?property_id=${project.property_id}`); }}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-emerald-500/20 hover:text-emerald-400 text-[10px] text-zinc-400"
            data-testid={`nav-study-${project.id}`}
            title={project.study_id ? "Étude liée" : "Créer l'étude"}>
            <Calculator className="w-3 h-3" /> Étude
          </button>
          <button onClick={(e) => { e.stopPropagation(); onNav(`/visites?property_id=${project.property_id}`); }}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-800 hover:bg-blue-500/20 hover:text-blue-400 text-[10px] text-zinc-400"
            data-testid={`nav-visit-${project.id}`}
            title="Visite technique">
            <ClipboardCheck className="w-3 h-3" /> Visite
          </button>
        </div>
      )}
      
      <div className="flex gap-1 mt-2">
        <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-500 hover:text-white" onClick={() => onEdit(project)}>
          <Edit className="w-3 h-3" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-500 hover:text-blue-400" onClick={() => onCompare(project.id)} title="Comparatif étude vs réalité" data-testid={`compare-project-${project.id}`}>
          <BarChart3 className="w-3 h-3" />
        </Button>
        {project.status === 'vendu' && (
          <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-500 hover:text-emerald-400" onClick={() => handlePdfExport(project.id)} data-testid={`export-project-pdf-${project.id}`} title="Rapport PDF">
            <FileDown className="w-3 h-3" />
          </Button>
        )}
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-amber-500 hover:text-amber-400"
          onClick={() => handleBankDossierExport(project.id)}
          data-testid={`export-bank-dossier-${project.id}`}
          title="Dossier de financement bancaire (PDF complet)"
        >
          <FileText className="w-3 h-3" />
        </Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-zinc-500 hover:text-red-400" onClick={() => onDelete(project.id)}>
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

const KanbanColumn = ({ status, projects, onEdit, onDelete, onDrop, onCompare, onNav, onDocAttached, highlightProjectId }) => {
  const config = STATUS_CONFIG[status];
  const [isDragOver, setIsDragOver] = useState(false);
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  
  const handleDragLeave = () => {
    setIsDragOver(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const projectId = e.dataTransfer.getData('projectId');
    if (projectId) {
      onDrop(projectId, status);
    }
  };
  
  return (
    <div 
      className={`flex-shrink-0 w-72 bg-zinc-900/50 rounded-lg border transition-colors ${
        isDragOver ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-zinc-800'
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="p-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
          <h3 className="font-medium text-white text-sm">{config.label}</h3>
        </div>
        <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded-full">
          {projects.length}
        </span>
      </div>
      <ScrollArea className="h-[calc(100vh-320px)]">
        <div className="p-3">
          {projects.map((project) => (
            <ProjectCard 
              key={project.id} 
              project={project}
              onEdit={onEdit}
              onDelete={onDelete}
              onCompare={onCompare}
              onNav={onNav}
              onDocAttached={onDocAttached}
              highlight={project.id === highlightProjectId}
            />
          ))}
          {projects.length === 0 && (
            <div className="text-center py-8 text-zinc-600 text-sm">
              Aucun projet
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

const ProjectForm = ({ project, properties, contacts, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    property_id: project?.property_id || '',
    status: project?.status || 'prospection',
    achat_date: project?.achat_date || '',
    vente_date: project?.vente_date || '',
    prix_achat_reel: project?.prix_achat_reel || '',
    prix_vente_reel: project?.prix_vente_reel || '',
    frais_reels: project?.frais_reels || '',
    travaux_reels: project?.travaux_reels || '',
    prix_mise_en_vente: project?.prix_mise_en_vente || '',
    date_mise_en_vente: project?.date_mise_en_vente || '',
    agent_immo_id: project?.agent_immo_id || '',
    nb_visites: project?.nb_visites || 0,
    nb_offres: project?.nb_offres || 0,
    duree_travaux_reelle_semaines: project?.duree_travaux_reelle_semaines || '',
    date_revente_respectee: project?.date_revente_respectee ?? null,
    notes: project?.notes || '',
  });
  const [loading, setLoading] = useState(false);

  const agentsImmo = (contacts || []).filter(c => (c.category || 'artisan') === 'agent_immo');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.property_id) {
      toast.error('Sélectionnez un bien');
      return;
    }
    setLoading(true);
    try {
      const data = {
        ...formData,
        prix_achat_reel: formData.prix_achat_reel ? parseFloat(formData.prix_achat_reel) : null,
        prix_vente_reel: formData.prix_vente_reel ? parseFloat(formData.prix_vente_reel) : null,
        frais_reels: formData.frais_reels ? parseFloat(formData.frais_reels) : null,
        travaux_reels: formData.travaux_reels ? parseFloat(formData.travaux_reels) : null,
        prix_mise_en_vente: formData.prix_mise_en_vente ? parseFloat(formData.prix_mise_en_vente) : null,
        nb_visites: formData.nb_visites ? parseInt(formData.nb_visites) : 0,
        nb_offres: formData.nb_offres ? parseInt(formData.nb_offres) : 0,
        duree_travaux_reelle_semaines: formData.duree_travaux_reelle_semaines ? parseInt(formData.duree_travaux_reelle_semaines) : null,
        agent_immo_id: formData.agent_immo_id || null,
        documents: project?.documents || [],
      };

      if (project?.id) {
        await axios.put(`${API}/projects/${project.id}`, data, { withCredentials: true });
        toast.success('Projet mis à jour');
      } else {
        await axios.post(`${API}/projects`, data, { withCredentials: true });
        toast.success('Projet créé');
      }
      onSave();
      onClose();
    } catch (err) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const status = formData.status;
  const showAchatFields = ['achete', 'en_renovation', 'en_vente', 'vendu'].includes(status);
  const showTravauxReels = ['en_renovation', 'en_vente', 'vendu'].includes(status);
  const showMiseEnVente = ['en_vente', 'vendu'].includes(status);
  const showVenteFields = status === 'vendu';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label className="text-zinc-300">Bien</Label>
          <Select value={formData.property_id} onValueChange={(v) => setFormData({ ...formData, property_id: v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white" data-testid="project-property-select">
              <SelectValue placeholder="Sélectionner" />
            </SelectTrigger>
            <SelectContent>
              {properties.map((prop) => (
                <SelectItem key={prop.id} value={prop.id}>{prop.title} - {prop.city}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label className="text-zinc-300">Statut</Label>
          <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                <SelectItem key={key} value={key}>{val.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* ACHAT fields (achete / en_renovation / en_vente / vendu) */}
        {showAchatFields && (
          <>
            <div>
              <Label className="text-zinc-300">Date d'achat</Label>
              <Input type="date" value={formData.achat_date} onChange={(e) => setFormData({ ...formData, achat_date: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
            </div>
            <div>
              <Label className="text-zinc-300">Prix d'achat réel (€)</Label>
              <Input type="number" value={formData.prix_achat_reel} onChange={(e) => setFormData({ ...formData, prix_achat_reel: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
            </div>
            <div>
              <Label className="text-zinc-300">Frais réels (€)</Label>
              <Input type="number" value={formData.frais_reels} onChange={(e) => setFormData({ ...formData, frais_reels: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
            </div>
          </>
        )}
        {showTravauxReels && (
          <div>
            <Label className="text-zinc-300">Travaux réels (€)</Label>
            <Input type="number" value={formData.travaux_reels} onChange={(e) => setFormData({ ...formData, travaux_reels: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
          </div>
        )}

        {/* EN VENTE block */}
        {showMiseEnVente && (
          <>
            <div className="sm:col-span-2 text-xs uppercase tracking-wider text-zinc-500 mt-2 border-t border-zinc-800 pt-3">Mise en vente</div>
            <div>
              <Label className="text-zinc-300">Prix de mise en vente (€)</Label>
              <Input type="number" value={formData.prix_mise_en_vente} onChange={(e) => setFormData({ ...formData, prix_mise_en_vente: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" data-testid="prix-mise-en-vente" />
            </div>
            <div>
              <Label className="text-zinc-300">Date de mise en vente</Label>
              <Input type="date" value={formData.date_mise_en_vente} onChange={(e) => setFormData({ ...formData, date_mise_en_vente: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
            </div>
            <div>
              <Label className="text-zinc-300">Agent immobilier</Label>
              <Select value={formData.agent_immo_id || 'none'} onValueChange={(v) => setFormData({ ...formData, agent_immo_id: v === 'none' ? '' : v })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white" data-testid="agent-immo-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {agentsImmo.map(a => <SelectItem key={a.id} value={a.id}>{a.name}{a.ville ? ` - ${a.ville}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-300">Nb visites</Label>
                <Input type="number" min="0" value={formData.nb_visites} onChange={(e) => setFormData({ ...formData, nb_visites: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
              </div>
              <div>
                <Label className="text-zinc-300">Nb offres reçues</Label>
                <Input type="number" min="0" value={formData.nb_offres} onChange={(e) => setFormData({ ...formData, nb_offres: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
              </div>
            </div>
          </>
        )}

        {/* VENDU block */}
        {showVenteFields && (
          <>
            <div className="sm:col-span-2 text-xs uppercase tracking-wider text-zinc-500 mt-2 border-t border-zinc-800 pt-3">Clôture vente</div>
            <div>
              <Label className="text-zinc-300">Date de vente</Label>
              <Input type="date" value={formData.vente_date} onChange={(e) => setFormData({ ...formData, vente_date: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
            </div>
            <div>
              <Label className="text-zinc-300">Prix de vente réel (€)</Label>
              <Input type="number" value={formData.prix_vente_reel} onChange={(e) => setFormData({ ...formData, prix_vente_reel: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" data-testid="prix-vente-reel" />
            </div>
            <div>
              <Label className="text-zinc-300">Durée travaux réelle (sem)</Label>
              <Input type="number" value={formData.duree_travaux_reelle_semaines} onChange={(e) => setFormData({ ...formData, duree_travaux_reelle_semaines: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white" />
            </div>
            <div>
              <Label className="text-zinc-300">Date revente respectée ?</Label>
              <Select value={formData.date_revente_respectee === null ? 'none' : (formData.date_revente_respectee ? 'yes' : 'no')} onValueChange={(v) => setFormData({ ...formData, date_revente_respectee: v === 'none' ? null : v === 'yes' })}>
                <SelectTrigger className="bg-zinc-950 border-zinc-800 text-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">N/A</SelectItem>
                  <SelectItem value="yes">Oui</SelectItem>
                  <SelectItem value="no">Non</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}

        <div className="sm:col-span-2">
          <Label className="text-zinc-300">Notes</Label>
          <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="bg-zinc-950 border-zinc-800 text-white min-h-[80px]" placeholder="Notes sur le projet..." />
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onClose} className="border-zinc-700 text-zinc-300">
          Annuler
        </Button>
        <Button type="submit" disabled={loading} className="text-white" style={{ backgroundColor: 'var(--accent-color, #10B981)' }} data-testid="save-project-button">
          {project?.id ? 'Mettre à jour' : 'Créer'}
        </Button>
      </div>
    </form>
  );
};

export const PipelinePage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [compareProjectId, setCompareProjectId] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [highlightProjectId, setHighlightProjectId] = useState(null);
  const [propertyFilter, setPropertyFilter] = useState(null);

  const handleCompare = (id) => {
    setCompareProjectId(id);
    setCompareOpen(true);
  };

  const fetchData = async () => {
    try {
      const [projectsRes, propsRes, artisansRes] = await Promise.all([
        axios.get(`${API}/projects`, { withCredentials: true }),
        axios.get(`${API}/properties`, { withCredentials: true }),
        axios.get(`${API}/artisans`, { withCredentials: true }),
      ]);
      setProjects(projectsRes.data);
      setProperties(propsRes.data);
      setContacts(artisansRes.data);
    } catch (err) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle cross-navigation params : ?project_id=... (open edit) or ?property_id=... (filter + highlight)
  useEffect(() => {
    const projectId = searchParams.get('project_id');
    const propId = searchParams.get('property_id');
    if (projectId && projects.length > 0) {
      const proj = projects.find(p => p.id === projectId);
      if (proj) {
        setSelectedProject(proj);
        setDialogOpen(true);
        setHighlightProjectId(projectId);
        setTimeout(() => {
          const el = document.getElementById(`project-${projectId}`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      }
      searchParams.delete('project_id');
      setSearchParams(searchParams, { replace: true });
    } else if (propId && projects.length > 0) {
      setPropertyFilter(propId);
      const existing = projects.find(p => p.property_id === propId);
      if (existing) {
        setHighlightProjectId(existing.id);
        setTimeout(() => {
          const el = document.getElementById(`project-${existing.id}`);
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      } else {
        // No existing project for this property → open create dialog pre-filled
        setSelectedProject({ property_id: propId, status: 'prospection' });
        setDialogOpen(true);
        toast.info("Aucun projet existant pour ce bien — création d'un nouveau");
      }
      searchParams.delete('property_id');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, searchParams]);

  const handleEdit = (project) => {
    setSelectedProject(project);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const res = await axios.delete(`${API}/projects/${id}`, { withCredentials: true });
      const c = res.data?.cascade || {};
      const parts = [];
      if (c.bdcs) parts.push(`${c.bdcs} BDC`);
      if (c.travaux) parts.push(`${c.travaux} suivi travaux`);
      if (c.appointments) parts.push(`${c.appointments} RDV`);
      if (c.files_detached) parts.push(`${c.files_detached} document(s) détaché(s)`);
      toast.success(parts.length > 0 ? `Projet supprimé · ${parts.join(', ')}` : 'Projet supprimé');
      fetchData();
    } catch (err) {
      console.error('Delete project error:', err);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleStatusChange = async (projectId, newStatus) => {
    try {
      await axios.patch(`${API}/projects/${projectId}/status?status=${newStatus}`, {}, { withCredentials: true });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (err) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedProject(null);
  };

  const getProjectsByStatus = (status) => {
    const list = projects.filter(p => p.status === status);
    if (propertyFilter) return list.filter(p => p.property_id === propertyFilter);
    return list;
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="pipeline-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white font-['Outfit'] tracking-tight">Pipeline des projets</h1>
          <p className="text-sm text-zinc-400 mt-1">Gérez vos projets immobiliers</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => { setLoading(true); fetchData(); }} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 text-sm transition-colors" data-testid="refresh-button">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Actualiser
          </button>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) setSelectedProject(null); }}>
            <DialogTrigger asChild>
              <Button
                className="bg-white text-black hover:bg-zinc-200"
                data-testid="add-project-button"
                onClick={() => { setSelectedProject(null); setDialogOpen(true); }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nouveau projet
              </Button>
            </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-xl max-h-[90vh] overflow-y-auto w-[95vw]">
            <DialogHeader>
              <DialogTitle className="text-white">
                {selectedProject ? 'Modifier le projet' : 'Nouveau projet'}
              </DialogTitle>
            </DialogHeader>
            <ProjectForm 
              project={selectedProject}
              properties={properties}
              contacts={contacts}
              onSave={fetchData} 
              onClose={handleCloseDialog}
            />
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {propertyFilter && (
        <div className="flex items-center justify-between bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-xs" data-testid="property-filter-banner">
          <span className="text-emerald-400">
            🔎 Filtré sur un bien : {properties.find(p => p.id === propertyFilter)?.title || propertyFilter}
          </span>
          <button onClick={() => { setPropertyFilter(null); setHighlightProjectId(null); }} className="text-emerald-300 hover:text-white text-xs underline">
            Retirer le filtre
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="flex-shrink-0 w-72 h-96 bg-zinc-800 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {Object.keys(STATUS_CONFIG).map((status) => (
            <KanbanColumn
              key={status}
              status={status}
              projects={getProjectsByStatus(status)}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onCompare={handleCompare}
              onDrop={handleStatusChange}
              onNav={navigate}
              onDocAttached={fetchData}
              highlightProjectId={highlightProjectId}
            />
          ))}
        </div>
      )}
      <ProjectComparisonDialog projectId={compareProjectId} open={compareOpen} onOpenChange={setCompareOpen} />
    </div>
  );
};
